import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle2, Info, XCircle } from 'lucide-react'
import { cn } from '../../lib/cn'

type ToastTone = 'default' | 'success' | 'danger'

interface ToastItem {
  id: number
  message: string
  tone: ToastTone
}

interface ToastContextValue {
  show: (message: string, opts?: { tone?: ToastTone }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with the provider that owns the context (same shape as contexts/AuthContext)
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}

const toneStyles: Record<ToastTone, string> = {
  default: 'text-text',
  success: 'text-success-fg',
  danger: 'text-danger-fg',
}

const toneIcons: Record<ToastTone, typeof Info> = {
  default: Info,
  success: CheckCircle2,
  danger: XCircle,
}

const DISMISS_MS = 4000

/** Transient confirmations (design-system §5.2 voice: quiet, warm). Errors that
 *  need action stay inline — toasts are for "it worked" moments. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  // Per-toast dismiss timers, held so hover can pause them (WCAG 2.2.1-adjacent
  // courtesy: give slow readers time) and unmount can clear them.
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    timers.current.delete(id)
    setToasts((ts) => ts.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (message: string, opts?: { tone?: ToastTone }) => {
      const id = nextId.current++
      setToasts((ts) => [...ts, { id, message, tone: opts?.tone ?? 'default' }])
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DISMISS_MS),
      )
    },
    [dismiss],
  )

  // Pause auto-dismiss while the pointer rests on a toast; restart in full on leave.
  const pause = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
  }, [])
  const resume = useCallback(
    (id: number) => {
      if (!timers.current.has(id)) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), DISMISS_MS),
        )
      }
    },
    [dismiss],
  )

  useEffect(() => {
    const held = timers.current
    return () => held.forEach((timer) => clearTimeout(timer))
  }, [])

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6"
      >
        {toasts.map((t) => {
          const Icon = toneIcons[t.tone]
          return (
            <div
              key={t.id}
              onMouseEnter={() => pause(t.id)}
              onMouseLeave={() => resume(t.id)}
              className={cn(
                'fireside-rise pointer-events-auto flex items-center gap-2 rounded-control',
                'bg-surface-raised px-4 py-2.5 text-sm ring-1 ring-border-warm shadow-warm',
                toneStyles[t.tone],
              )}
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
              {t.message}
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}
